<template>
  <div class="container mt-5">
    <h2 class="text-center mb-4">Manage Games</h2>

    <!-- Game Creation Form -->
    <form @submit.prevent="createGames">
      <div class="form-group">
        <label>Select Locations</label>
        <div>
          <div v-for="location in activeLocations" :key="location.id" class="form-check">
            <input
              type="checkbox"
              :id="location.id"
              :value="location.id"
              v-model="selectedLocations"
              class="form-check-input"
            />
            <label :for="location.id" class="form-check-label">{{ location.name }}</label>
          </div>
        </div>
      </div>
      <div class="form-group">
        <label for="date">Select Date</label>
        <input type="date" v-model="selectedDate" class="form-control" id="date" required />
      </div>
      <button type="submit" class="btn btn-primary mt-3">Create New Games</button>
    </form>

    <!-- Existing Games Table Grouped by Date -->
    <div v-if="currentView === 'ManageGames'" class="mt-5">
      <div v-if="!showEnterCorrectAnswers">
        <h3>Existing Games</h3>
        <div v-for="(gamesOnDate, date) in sortedGroupedGames" :key="date" class="mb-4 game-item">
          <h4>{{ date }}</h4>
          <p>Locations: {{ gamesOnDate.map((game) => game.locationName).join(', ') }}</p>
          <p class="text-muted small">Game IDs: {{ gamesOnDate.map((game) => game.id).join(', ') }}</p>
          <button @click="enterCorrectAnswers(gamesOnDate.map((game) => game.id))" class="btn btn-success btn-sm button-margin">
            Enter Correct Answers
          </button>
          <button @click="copyGame(gamesOnDate[0].id)" class="btn btn-info btn-sm ml-2 button-margin">
            Copy Game
          </button>
          <br>
          <button @click="deleteGames(gamesOnDate.map((game) => game.id))" class="btn btn-danger btn-sm ml-2 button-margin">
            Delete Game
          </button>
        </div>
      </div>
      <div v-else>
        <!-- Enter Correct Answers component -->
        <EnterCorrectAnswers :gameIds="selectedGameIds" @backToGames="backToManageGames" />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { collection, getDocs, addDoc, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '@/firebase';
import EnterCorrectAnswers from './EnterCorrectAnswers.vue';
import moment from 'moment-timezone';

export default {
  name: 'ManageGames',
  components: {
    EnterCorrectAnswers,
  },
  setup() {
    const selectedLocations = ref([]);
    const selectedDate = ref('');
    const activeLocations = ref([]);
    const groupedGames = ref({});
    const currentView = ref('ManageGames');
    const selectedGameIds = ref([]);
    const showEnterCorrectAnswers = ref(false);

    const fetchActiveLocations = async () => {
      try {
        const locationsSnapshot = await getDocs(collection(db, 'locations'));
        activeLocations.value = locationsSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((location) => location.isActive);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };

    const fetchGames = async () => {
      try {
        const gamesSnapshot = await getDocs(collection(db, 'games'));
        const gamesData = gamesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          locationName: activeLocations.value.find((loc) => loc.id === doc.data().location)?.name || 'Unknown Location',
        }));

        // Group games by date
        groupedGames.value = gamesData.reduce((acc, game) => {
          if (!acc[game.date]) {
            acc[game.date] = [];
          }
          acc[game.date].push(game);
          return acc;
        }, {});
      } catch (error) {
        console.error('Error fetching games:', error);
      }
    };

    const sortedGroupedGames = computed(() => {
  // Sort keys (dates) in descending order
  const sortedKeys = Object.keys(groupedGames.value).sort((a, b) => new Date(b) - new Date(a));
  return sortedKeys.reduce((acc, key) => {
    acc[key] = groupedGames.value[key];
    return acc;
  }, {});
});

const createGames = async () => {
  if (selectedLocations.value.length > 0 && selectedDate.value) {
    try {
      // Convert the selected date to Eastern Time (ET)
      const gameDate = moment.tz(selectedDate.value, 'America/New_York').startOf('day').format('YYYY-MM-DD');

      for (const locationId of selectedLocations.value) {
        const gameData = {
          location: locationId,
          date: gameDate, // Store the ET date as a string
          rounds: [],
        };

        await addDoc(collection(db, 'games'), gameData);
      }
      alert('Games created successfully!');
      await fetchGames(); // Refresh the games list
    } catch (error) {
      console.error('Error creating games:', error);
      alert('Error creating games. Please try again.');
    }
  } else {
    alert('Please select at least one location and a date to create games.');
  }
};

    const deleteGames = async (gameIds) => {
      if (confirm('Are you sure you want to delete these games?')) {
        try {
          for (const gameId of gameIds) {
            await deleteDoc(doc(db, 'games', gameId));
          }
          alert('Games deleted successfully.');
          await fetchGames(); // Refresh the games list
        } catch (error) {
          console.error('Error deleting games:', error);
          alert('Error deleting games. Please try again.');
        }
      }
    };

    const enterCorrectAnswers = (gameIds) => {
      if (!gameIds || gameIds.length === 0) {
        console.error("No game IDs provided.");
        return;
      }
      selectedGameIds.value = gameIds;
      showEnterCorrectAnswers.value = true;
    };

    const backToManageGames = () => {
      showEnterCorrectAnswers.value = false;
      selectedGameIds.value = [];
    };

    const copyGame = async (gameId) => {
      try {
        const selectedLocationsCopy = [...selectedLocations.value];

        // Convert the selected date to Eastern Time (ET)
        const gameDateET = moment.tz(selectedDate.value, 'America/New_York').startOf('day').format('YYYY-MM-DD');

        if (selectedLocationsCopy.length === 0 || !gameDateET) {
          alert('Please select locations and a date before copying.');
          return;
        }

        // Fetch the original game rounds
        const originalRoundsSnapshot = await getDocs(collection(db, `games/${gameId}/rounds`));
        const originalRounds = originalRoundsSnapshot.docs.map((doc) => {
          const roundData = doc.data();
          console.log('Original round data:', roundData); // Debug log
          return roundData;
        });

        for (const locationId of selectedLocationsCopy) {
          const gameData = {
            location: locationId,
            date: gameDateET,
            rounds: [],
          };

          const newGameDocRef = await addDoc(collection(db, 'games'), gameData);
          console.log(`New Game created with ID: ${newGameDocRef.id}`);

          // Copy the rounds to the new game with isActive set to false
          const batch = writeBatch(db);
          originalRounds.forEach((round) => {
            const newRoundDocRef = doc(db, `games/${newGameDocRef.id}/rounds`, `round${round.roundNumber}`);
            // Force isActive to false and log the new round data
            const newRoundData = { ...round, isActive: false };
            console.log('New round data:', newRoundData); // Debug log
            batch.set(newRoundDocRef, newRoundData);
          });

          await batch.commit();
          console.log(`Rounds copied to new game with ID: ${newGameDocRef.id}`);
        }

        await fetchGames();
        alert('Game copied successfully!');
      } catch (error) {
        console.error('Error copying game:', error);
        alert('Error copying game. Please try again.');
      }
    };

    onMounted(async () => {
      await fetchActiveLocations();
      await fetchGames();
    });

    return {
      selectedLocations,
      selectedDate,
      activeLocations,
      groupedGames,
      currentView,
      selectedGameIds,
      showEnterCorrectAnswers,
      sortedGroupedGames,
      createGames,
      deleteGames,
      enterCorrectAnswers,
      backToManageGames,
      copyGame,
    };
  },
};
</script>


<style scoped>
.button-margin {
  margin: 5px;
}

.game-item:nth-child(odd) {
  background-color: #f9f9f9; /* Light grey */
}

.game-item:nth-child(even) {
  background-color: #ffffff; /* White */
}

.game-item {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
}
</style>
